import React from "react";
import "../../resources/scss/button/main.scss";

type ElementProps<T extends React.ElementType> =
  React.ComponentPropsWithoutRef<T> & {
    ref?: React.Ref<Element>;
  };

interface BaseProps {
  sx?: React.CSSProperties;
  className?: string;

  size?: "large" | "medium" | "small" | "very_small";
  fontWeight?: "light" | "medium" | "bold";
  type?:
    | "contained_main"
    | "contained_light"
    | "contained_dark"
    | "contained_white"
    | "contained_grey"
    | "outlined"
    | "text"
    | "text_elevated"
    | "text_grey_error"
    | "text_grey";
  htmlType?: "button" | "submit" | "reset";
  fullWidth?: boolean;
  shadow?: boolean;

  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;

  children: React.ReactNode;
}

// Default is button, but user can override with `as`
type ButtonProps<T extends React.ElementType = "button"> = BaseProps & {
  as?: T;
} & Omit<ElementProps<T>, keyof BaseProps>;

const Button = <T extends React.ElementType = "button">({
  as,
  sx,
  className = "",
  fontWeight = "light",
  size = "medium",
  type = "contained_main",
  htmlType = "button",
  fullWidth = false,
  startIcon,
  endIcon,
  loading = false,
  disabled = false,
  shadow = false,
  children,
  ...rest
}: ButtonProps<T>) => {
  const Component = as || "button";

  const classes = [
    "common_btn",
    `common_btn_type_${type}`,
    "common_btn_size",
    `common_btn_size_${size}`,
    disabled ? `common_btn_disabled common_btn_disabled_${type}` : "",
    loading ? "common_btn_loading" : "",
    fullWidth ? "common_btn_fullWidth" : "",
    fontWeight ? `common_btn_fontWeight_${fontWeight}` : "",
    shadow ? `common_btn_shadow` : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const commonProps: any = {
    style: sx,
    className: classes,
    disabled,
    "aria-disabled": disabled || loading ? true : undefined,
    tabIndex: disabled ? -1 : undefined,
    role: Component === "div" ? "button" : undefined,
    ...(Component === "button" ? { type: htmlType } : {}),
    ...rest,
  };

  return (
    <Component {...commonProps}>
      {loading ? (
        <div className="common_btn_loading_dotsLoading" />
      ) : (
        <>
          {startIcon && (
            <span
              className={`common_btn_icon${
                size === "small" ? " common_btn_icon--small" : ""
              }`}
            >
              {startIcon}
            </span>
          )}

          {children}

          {endIcon && (
            <span
              className={`common_btn_icon${
                size === "small" ? " common_btn_icon--small" : ""
              }`}
            >
              {endIcon}
            </span>
          )}
        </>
      )}
    </Component>
  );
};

export default Button;
